import React, { useState, useEffect } from "react";
import CustomModal from "./CustomModal";
import AuthInput from "../Input/AuthInput";
import { useDispatch } from "react-redux";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import { AdjustItemQtyApi } from "../../ApiRequests";
import AuthBtn from "../Buttons/AuthBtn";
import { fetchProducts } from "../../store/Slices/ProductSlice";

const AdjustItemQtyModal = ({ Open, setOpen, product }) => {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (Open && product?._id) {
      setDelta("");
      setReason("");
    }
  }, [Open, product?._id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const deltaNum = Number(delta);
    if (!product?._id) {
      WarningToast("No item selected");
      return;
    }
    if (!Number.isFinite(deltaNum) || deltaNum === 0) {
      WarningToast("Enter a non-zero change (use minus to reduce stock)");
      return;
    }
    const next = (Number(product.qty) || 0) + deltaNum;
    const nextIn = (Number(product.in_qty) || 0) + deltaNum;
    if (next < 0) {
      WarningToast("Stock (qty) cannot go below zero");
      return;
    }
    if (nextIn < 0) {
      WarningToast(
        "In-quantity cannot go below zero — decrease amount is too large"
      );
      return;
    }
    if (!reason || reason.trim().length < 3) {
      WarningToast("Reason must be at least 3 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await AdjustItemQtyApi(product._id, {
        delta: deltaNum,
        reason: reason.trim(),
      });
      if (response.data.success) {
        setOpen(false);
        SuccessToast("Stock updated and logged");
        dispatch(fetchProducts());
      } else {
        ErrorToast(response.data.error?.msg || "Request failed");
      }
    } catch (err) {
      ErrorToast(
        err.response?.data?.error?.msg ||
          err.message ||
          "Failed to adjust quantity"
      );
    }
    setLoading(false);
  };

  if (!product?._id) return null;

  return (
    <CustomModal open={Open} setOpen={setOpen}>
      <div className="pb-8 max-h-[85vh] overflow-y-auto">
        <h1 className="w-full text-center font-[700] text-2xl py-6 px-4">
          Adjust stock quantity
        </h1>
        <p className="text-center text-sm text-inkSecondary px-6 mb-4">
          <span className="font-semibold">{product.name}</span>
          <br />
          Current <span className="font-semibold">qty</span>:{" "}
          <span className="font-bold text-main">{product.qty ?? 0}</span>
          {" · "}
          <span className="font-semibold">in qty</span>:{" "}
          <span className="font-bold text-main">{product.in_qty ?? 0}</span>
          <span className="block mt-1 text-xs">
            Same change applies to both qty and in-quantity.
          </span>
        </p>
        <div className="flex flex-col gap-y-3 px-8">
          <AuthInput
            label="Change in quantity (+ or −)"
            placeholder="e.g. 10 or -5"
            Type="number"
            Value={delta}
            setValue={setDelta}
            Width="w-full"
          />
          <div className="relative w-full font-[Quicksand]">
            <p className="absolute top-[-11px] left-3 w-fit bg-white h-[13px] text-[15px] font-bold text-main z-[1]">
              Reason (required for log)
            </p>
            <textarea
              className="px-3 py-2 border border-line bg-card text-ink rounded-lg w-full min-h-[100px] outline-none text-sm focus:border-main focus:ring-2 focus:ring-main/15"
              placeholder="Why is stock changing? (damage, recount, correction…)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-center items-center gap-x-2 mt-6 px-5">
          {loading ? (
            <AddingLightLoader />
          ) : (
            <>
              <AuthBtn title={"Save & log"} onSubmit={onSubmit} type={1} />
              <AuthBtn
                title={"Cancel"}
                onSubmit={() => setOpen(false)}
                type={2}
              />
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default AdjustItemQtyModal;
